/**
 * Task EE (part F) — statutory contribution PRESETS per country.
 *
 * These are editable EXAMPLE templates the owner loads on demand; they are never applied automatically and the
 * engine (payroll-ee.ts) encodes no country law. Every rate here must be checked by the owner / their accountant.
 * Research notes, sources and verification status per item: docs/PAYROLL-PRESETS.md (retrieved 2026-10-04).
 *
 * Conventions: amounts in integer MINOR units of the preset currency (JPY / KRW / VND exponent 0; MYR, SGD, HKD,
 * TWD, CNY, THB, IDR, USD, GBP exponent 2). Rates in basis points (100 = 1%; a few are fractional, e.g. 359.5).
 * Items whose rate could not be verified carry 0 and a "TODO verify" note rather than a guess.
 */
import type { IdField, StatutoryItem, StatWageBase, StatRounding, StatAgeBand, StatBracket } from "./payroll-ee.js";

export interface PayrollPreset {
  country: string;
  name: { en: string; zh: string };
  currency: string;
  retrievedOn: string;
  disclaimer: { en: string; zh: string };
  items: StatutoryItem[];
  idFields: IdField[];
  notes: { en: string; zh: string };
}

export const PRESET_DISCLAIMER = {
  en: "Example template — verify with your local accountant / the official source. The system does not guarantee rates are correct.",
  zh: "示例模板 — 请与当地会计/官方核对，系统不保证税率正确",
};

const RETRIEVED = "2026-10-04";

const BASE_ALL: StatWageBase = { basic: true, hourly: true, commission: true, allowances: true, adjustments: false };
const BASE_FIXED: StatWageBase = { basic: true, hourly: true, commission: false, allowances: true, adjustments: false };
const R_CENT: StatRounding = { mode: "nearest", unitMinor: 1 };

interface ItemSpec {
  id: string;
  name: { en: string; zh: string; local?: string };
  country: string;
  method?: StatutoryItem["method"];
  employeeBp?: number;
  employerBp?: number;
  ageBands?: StatAgeBand[];
  brackets?: StatBracket[];
  base?: StatWageBase;
  wageFloorMinor?: number | null;
  wageCeilingMinor?: number | null;
  capEmployeeMinor?: number | null;
  capEmployerMinor?: number | null;
  rounding?: StatRounding;
  employmentTypes?: string[];
  residency?: string[];
  note: string;
  source: string;
}

/** Builds a complete StatutoryItem (every field present, fresh arrays / objects) from a compact spec. */
function item(s: ItemSpec): StatutoryItem {
  return {
    id: s.id,
    name: { ...s.name },
    country: s.country,
    active: true,
    method: s.method ?? "percent",
    employeeBp: s.employeeBp ?? 0,
    employerBp: s.employerBp ?? 0,
    ageBands: (s.ageBands ?? []).map((b) => ({ ...b })),
    brackets: (s.brackets ?? []).map((b) => ({ ...b })),
    fixedEmployeeMinor: 0,
    fixedEmployerMinor: 0,
    base: { ...(s.base ?? BASE_ALL) },
    wageFloorMinor: s.wageFloorMinor ?? null,
    wageCeilingMinor: s.wageCeilingMinor ?? null,
    capEmployeeMinor: s.capEmployeeMinor ?? null,
    capEmployerMinor: s.capEmployerMinor ?? null,
    rounding: { ...(s.rounding ?? R_CENT) },
    employmentTypes: [...(s.employmentTypes ?? [])],
    residency: [...(s.residency ?? [])],
    optOutStaffIds: [],
    note: s.note,
    source: s.source,
  };
}

const idf = (key: string, en: string, zh: string, local?: string): IdField => ({ key, label: local ? { en, zh, local } : { en, zh } });

const PWC = (c: string): string => "https://taxsummaries.pwc.com/" + c + "/individual/other-taxes";

// ------------------------------------------------------------------------------------------------ MY
const MY: PayrollPreset = {
  country: "MY",
  name: { en: "Malaysia", zh: "马来西亚" },
  currency: "MYR",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "my_epf", country: "MY", name: { en: "EPF", zh: "公积金 (EPF)", local: "KWSP" },
      employeeBp: 1100, employerBp: 1300,
      ageBands: [
        { minAge: 0, maxAge: 59, employeeBp: 1100, employerBp: 1300 },
        { minAge: 60, maxAge: null, employeeBp: 0, employerBp: 400 },
      ],
      rounding: { mode: "up", unitMinor: 100 }, residency: ["local"],
      note: "Malaysian citizens: 11% employee / 13% employer for wages <= RM5,000; employer 12% when wages > RM5,000 (set employerBp / the band to 1200 for those staff, or add a 2nd item). Age 60+: 0% / 4%. PRs and pre-1998 electing non-citizens have different 60+ rates (5.5% / 6.5%, or 6% above RM5,000). Official EPF uses the Third Schedule wage-band table (rounded up to the ringgit); this % model is an approximation.",
      source: PWC("malaysia"),
    }),
    item({
      id: "my_epf_foreign", country: "MY", name: { en: "EPF (foreign workers)", zh: "公积金 (外籍员工)", local: "KWSP" },
      employeeBp: 200, employerBp: 200, rounding: { mode: "up", unitMinor: 100 }, residency: ["foreign"],
      note: "Non-Malaysian employees: 2% employee / 2% employer, no wage limit. Some categories (e.g. domestic workers) are excluded — check KWSP.",
      source: PWC("malaysia"),
    }),
    item({
      id: "my_socso", country: "MY", name: { en: "SOCSO", zh: "社险 (SOCSO)", local: "PERKESO" },
      method: "bracket", brackets: [], wageCeilingMinor: 600000, capEmployeeMinor: 2975, capEmployerMinor: 10415, base: { ...BASE_ALL, overtime: true },
      note: "TODO: paste the official PERKESO First Category contribution table (Act 4 Third Schedule) via the CSV import — brackets are empty so nothing is computed yet. Wage ceiling RM6,000 since 1 Oct 2024; maximum RM29.75 employee / RM104.15 employer. Staff 60+ (and foreign workers) are Employment Injury category only (employer-only, different table).",
      source: "https://www.perkeso.gov.my/en/rate-of-contribution.html",
    }),
    item({
      id: "my_eis", country: "MY", name: { en: "EIS", zh: "就业保险 (EIS)", local: "SIP" },
      employeeBp: 20, employerBp: 20, wageCeilingMinor: 600000, capEmployeeMinor: 1190, capEmployerMinor: 1190, base: { ...BASE_ALL, overtime: true },
      ageBands: [{ minAge: 60, maxAge: null, employeeBp: 0, employerBp: 0 }], residency: ["local"],
      note: "0.2% employee / 0.2% employer, wage ceiling RM6,000, max RM11.90 each. Officially a wage-band table (amounts based on band mid-points) — this % model can differ by a few sen. Not for staff 60+ (or 57+ with no prior contributions).",
      source: PWC("malaysia"),
    }),
    item({
      id: "my_nis", country: "MY", name: { en: "SOCSO Non-Employment Injury Scheme", zh: "社险 非工伤计划 (NIS)", local: "SBBP" },
      employeeBp: 0, employerBp: 0, wageCeilingMinor: 600000,
      note: "TODO verify: new from 1 Jun 2026, reported as 0.75% of wage (cap RM44.65, ceiling RM6,000) for the first two years, rising to 1.25% by year 6. Which party pays was not confirmed — rates left at 0.",
      source: PWC("malaysia"),
    }),
    item({
      id: "my_pcb", country: "MY", name: { en: "Income tax (PCB / MTD)", zh: "所得税预扣 (PCB/MTD)", local: "PCB" },
      method: "bracket", brackets: [],
      note: "Income tax (PCB/MTD) is NOT computed — paste your table or enter manually (e.g. as a manual deduction from the LHDN e-PCB calculator).",
      source: "https://www.hasil.gov.my/",
    }),
  ],
  idFields: [
    idf("ic_no", "NRIC / Passport no.", "身份证/护照号码"),
    idf("epf_no", "EPF no.", "公积金号码"),
    idf("socso_no", "SOCSO no.", "社险号码"),
    idf("tax_no", "Income tax no.", "所得税号码"),
  ],
  notes: {
    en: "EPF employer share drops to 12% above RM5,000. SOCSO needs the official bracket table pasted in. HRD Corp levy and PCB are not included.",
    zh: "月薪超过 RM5,000 时公积金雇主比例为 12%。社险需贴入官方级距表。人力资源发展基金 (HRD Corp) 与 PCB 未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ SG
const SG: PayrollPreset = {
  country: "SG",
  name: { en: "Singapore", zh: "新加坡" },
  currency: "SGD",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "sg_cpf", country: "SG", name: { en: "CPF", zh: "公积金 (CPF)", local: "CPF" },
      employeeBp: 2000, employerBp: 1700,
      ageBands: [
        { minAge: 0, maxAge: 55, employeeBp: 2000, employerBp: 1700 },
        { minAge: 56, maxAge: 60, employeeBp: 1800, employerBp: 1600 },
        { minAge: 61, maxAge: 65, employeeBp: 1250, employerBp: 1250 },
        { minAge: 66, maxAge: 70, employeeBp: 750, employerBp: 900 },
        { minAge: 71, maxAge: null, employeeBp: 500, employerBp: 750 },
      ],
      wageFloorMinor: 50000, wageCeilingMinor: 800000,
      rounding: { mode: "down", unitMinor: 100 }, residency: ["local"],
      note: "Table 1 (citizens / SPR 3rd year+) from 1 Jan 2026; OW ceiling S$8,000/month. New rates apply from the month AFTER the 55th/60th/65th/70th birthday (bands here use whole years). Wages <= S$50: nil; S$50–500: employer only; S$500–750: employee share phases in (0.6 x (TW - 500) for age <= 55) — the phase-in is NOT modelled. Employee share rounds down to the dollar; official employer share = total rounded to the nearest dollar - employee share (may differ by S$1). AW ceiling and SPR 1st/2nd-year graduated rates not modelled.",
      source: "https://www.cpf.gov.sg/content/dam/web/employer/employer-obligations/documents/CPFcontributionratesfrom1Jan2026.pdf",
    }),
  ],
  idFields: [
    idf("nric", "NRIC / FIN", "身份证/FIN 号码"),
    idf("cpf_no", "CPF account no.", "公积金账号"),
  ],
  notes: {
    en: "CPF applies to Singapore citizens and PRs only (foreign employees: none). Skills Development Levy (SDL), foreign worker levy and income tax are not included.",
    zh: "CPF 只适用于新加坡公民及永久居民（外籍员工无需缴交）。技能发展税 (SDL)、外劳税与所得税未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ HK
const HK: PayrollPreset = {
  country: "HK",
  name: { en: "Hong Kong", zh: "香港" },
  currency: "HKD",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "hk_mpf", country: "HK", name: { en: "MPF", zh: "强积金 (MPF)", local: "強積金" },
      employeeBp: 500, employerBp: 500,
      ageBands: [
        { minAge: 0, maxAge: 17, employeeBp: 0, employerBp: 0 },
        { minAge: 65, maxAge: null, employeeBp: 0, employerBp: 0 },
      ],
      wageFloorMinor: 710000, wageCeilingMinor: 3000000, capEmployeeMinor: 150000, capEmployerMinor: 150000,
      note: "Monthly-paid: 5% / 5% of relevant income. Below HK$7,100 the employee pays nothing (employer still 5%); above HK$30,000 both are capped at HK$1,500. Relevant income includes commission, bonuses and cash allowances. Covers employees aged 18–64. The new-joiner employee contribution holiday (first 30 days) is not modelled. Non-monthly staff use daily levels HK$280 / HK$1,000.",
      source: "https://www.mpfa.org.hk/en/mpf-system/mandatory-contributions/employees",
    }),
  ],
  idFields: [
    idf("hkid", "HKID no.", "香港身份证号码"),
    idf("mpf_scheme_no", "MPF scheme / member no.", "强积金计划/成员编号"),
  ],
  notes: {
    en: "Salaries tax is assessed annually and is not withheld through payroll; not included.",
    zh: "薪俸税按年评税，不经由薪资预扣，故未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ TW
const TW: PayrollPreset = {
  country: "TW",
  name: { en: "Taiwan", zh: "台湾" },
  currency: "TWD",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "tw_labor_ins", country: "TW", name: { en: "Labour insurance (incl. employment insurance)", zh: "劳保（含就业保险）", local: "勞保" },
      employeeBp: 0, employerBp: 0, base: BASE_FIXED,
      note: "TODO verify: rates left at 0. Contributions are based on the insured-salary grade table (投保薪資分級表), split employee 20% / employer 70% / government 10%. Last known total 11.5% ordinary + 1% employment insurance — confirm the 2026 rate with BLI and paste the grade table as brackets. Occupational accident insurance (employer, by industry) not included.",
      source: "https://www.bli.gov.tw/",
    }),
    item({
      id: "tw_nhi", country: "TW", name: { en: "National Health Insurance", zh: "全民健保", local: "健保" },
      employeeBp: 0, employerBp: 0, base: BASE_FIXED,
      note: "TODO verify: rates left at 0. Based on the insured-amount grade table; employee 30% x (1 + dependants, max 3), employer 60% x (1 + average dependants). Last known premium rate 5.17% — confirm with NHIA. Supplementary premium (2.11%) on large bonuses not modelled.",
      source: "https://www.nhi.gov.tw/",
    }),
    item({
      id: "tw_labor_pension", country: "TW", name: { en: "Labour pension (employer)", zh: "劳退（雇主提缴）", local: "勞退" },
      employeeBp: 0, employerBp: 600, base: BASE_FIXED, residency: ["local"],
      note: "Employer must contribute at least 6% of the monthly contribution wage (月提繳工資, a grade table — % of actual wage is an approximation). Employee voluntary contribution (0–6%) can be added per staff. Foreign workers are generally not covered (PR holders / foreign spouses are). Statutory minimum per the Labor Pension Act; not re-fetched on 2026-10-04.",
      source: "https://www.bli.gov.tw/",
    }),
  ],
  idFields: [
    idf("national_id", "National ID no.", "身分证字号", "身分證字號"),
    idf("arc_no", "ARC / UI no. (foreigners)", "居留证号（外籍）", "居留證號"),
  ],
  notes: {
    en: "Taiwan contributions use insured-salary GRADE tables; paste the current BLI / NHIA tables as brackets for exact amounts. Income tax withholding is not included.",
    zh: "台湾的劳健保以投保薪资分级表计算，请贴入劳保局/健保署最新分级表作为级距。薪资所得扣缴未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ CN
const CN_SH = "Shanghai example (city-dependent). ";
const CN: PayrollPreset = {
  country: "CN",
  name: { en: "China (Shanghai example)", zh: "中国（上海示例）" },
  currency: "CNY",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "cn_pension", country: "CN", name: { en: "Pension insurance", zh: "养老保险" },
      employeeBp: 800, employerBp: 1600, wageCeilingMinor: 3730200,
      note: CN_SH + "8% / 16%, base capped at CNY 37,302 (valid as of 30 Jun 2026; adjusted around July each year). Wages below the city floor are contributed on the floor base — not modelled.",
      source: PWC("peoples-republic-of-china"),
    }),
    item({
      id: "cn_medical", country: "CN", name: { en: "Medical insurance (incl. maternity)", zh: "医疗保险（含生育）" },
      employeeBp: 200, employerBp: 900, wageCeilingMinor: 3730200,
      note: CN_SH + "2% / 9% (maternity merged into medical). For comparison Beijing 2% / 9.8%, Guangzhou 2% / 6.85%.",
      source: PWC("peoples-republic-of-china"),
    }),
    item({
      id: "cn_unemployment", country: "CN", name: { en: "Unemployment insurance", zh: "失业保险" },
      employeeBp: 50, employerBp: 50, wageCeilingMinor: 3730200,
      note: CN_SH + "0.5% / 0.5%.",
      source: PWC("peoples-republic-of-china"),
    }),
    item({
      id: "cn_injury", country: "CN", name: { en: "Work injury insurance", zh: "工伤保险" },
      employeeBp: 0, employerBp: 20, wageCeilingMinor: 3730200,
      note: CN_SH + "Employer only, 0.2%–1.9% by industry risk class and floating rate — 0.2% (lowest) used; confirm your assigned rate.",
      source: PWC("peoples-republic-of-china"),
    }),
    item({
      id: "cn_housing_fund", country: "CN", name: { en: "Housing provident fund", zh: "住房公积金" },
      employeeBp: 700, employerBp: 700, wageCeilingMinor: 3730200, rounding: { mode: "nearest", unitMinor: 100 },
      note: "TODO verify: Shanghai basic housing fund commonly 7% / 7% (employers may choose 5%–7%; supplementary fund optional) — rate and base ceiling NOT re-verified on 2026-10-04 (the HPF ceiling can differ from the social-insurance ceiling).",
      source: "https://www.shgjj.com/",
    }),
  ],
  idFields: [
    idf("resident_id", "Resident ID no.", "身份证号"),
    idf("social_insurance_no", "Social insurance no.", "社保号"),
    idf("housing_fund_no", "Housing fund account no.", "公积金账号"),
  ],
  notes: {
    en: "五险一金 rates, bases (typically floor 60% / ceiling 300% of the local average wage) and housing-fund rates differ by city and change every year. This is a Shanghai example only. IIT (individual income tax) withholding is not computed.",
    zh: "五险一金的费率、缴费基数（上下限一般为当地社平工资的 60%/300%）及公积金比例因城市而异且每年调整。此为上海示例。个人所得税预扣未计算。",
  },
};

// ------------------------------------------------------------------------------------------------ JP
const JP: PayrollPreset = {
  country: "JP",
  name: { en: "Japan (Tokyo, Kyokai Kenpo example)", zh: "日本（东京协会健保示例）" },
  currency: "JPY",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "jp_health", country: "JP", name: { en: "Health insurance", zh: "健康保险", local: "健康保険" },
      employeeBp: 492.5, employerBp: 492.5, base: BASE_FIXED, wageCeilingMinor: 1390000,
      note: "Kyokai Kenpo Tokyo from Mar 2026: 9.85% split 50/50 (4.925% each). The rate differs per prefecture and per health-insurance society. Calculated on the standard monthly remuneration grade (標準報酬月額, max JPY 1.39m) — % of actual wage is an approximation. Bonuses are assessed separately.",
      source: PWC("japan"),
    }),
    item({
      id: "jp_ltc", country: "JP", name: { en: "Long-term care insurance (age 40–64)", zh: "介护保险（40–64 岁）", local: "介護保険" },
      employeeBp: 0, employerBp: 0, ageBands: [{ minAge: 40, maxAge: 64, employeeBp: 81, employerBp: 81 }],
      base: BASE_FIXED, wageCeilingMinor: 1390000,
      note: "Only for employees aged 40–64: 1.62% split 50/50 (0.81% each) from Mar 2026 (Kyokai Kenpo). Staff without a birth date get 0.",
      source: PWC("japan"),
    }),
    item({
      id: "jp_child_support", country: "JP", name: { en: "Child & childcare support levy", zh: "儿童育儿支援金", local: "子ども・子育て支援金" },
      employeeBp: 11.5, employerBp: 11.5, base: BASE_FIXED, wageCeilingMinor: 1390000,
      note: "New from Apr 2026, collected with health insurance: 0.23% split 50/50 (0.115% each). The separate employer-only child allowance contribution (子ども・子育て拠出金, 0.36%) is not included.",
      source: PWC("japan"),
    }),
    item({
      id: "jp_pension", country: "JP", name: { en: "Employees' pension insurance", zh: "厚生年金", local: "厚生年金保険" },
      employeeBp: 915, employerBp: 915, base: BASE_FIXED, wageCeilingMinor: 650000,
      note: "18.3% split 50/50 (9.15% each) on the standard monthly remuneration grade (max JPY 650,000). Nationwide rate.",
      source: PWC("japan"),
    }),
    item({
      id: "jp_employment_ins", country: "JP", name: { en: "Employment insurance", zh: "雇用保险", local: "雇用保険" },
      employeeBp: 50, employerBp: 0, base: BASE_ALL,
      note: "General business from Apr 2026: employee 0.5%. Employer share TODO verify (left at 0) — check the MHLW FY2026 rate table (agriculture / construction rates are higher). Based on actual wages (no ceiling).",
      source: PWC("japan"),
    }),
  ],
  idFields: [
    idf("my_number", "My Number", "个人编号", "マイナンバー"),
    idf("basic_pension_no", "Basic pension no.", "基础年金编号", "基礎年金番号"),
    idf("employment_ins_no", "Employment insurance no.", "雇用保险被保险者编号", "雇用保険被保険者番号"),
  ],
  notes: {
    en: "Health insurance varies by prefecture / society; pension and health use standard monthly remuneration grades. Workers' accident insurance (employer only, by industry) and income / resident tax withholding are not included.",
    zh: "健康保险费率因都道府县/健保组合而异；年金与健保按标准报酬月额分级计算。劳灾保险（雇主负担，按行业）以及所得税/住民税预扣未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ KR
const KR: PayrollPreset = {
  country: "KR",
  name: { en: "South Korea", zh: "韩国" },
  currency: "KRW",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "kr_pension", country: "KR", name: { en: "National pension", zh: "国民年金", local: "국민연금" },
      employeeBp: 475, employerBp: 475, base: BASE_FIXED, wageCeilingMinor: 6590000,
      rounding: { mode: "down", unitMinor: 10 },
      note: "2026: 9.5% split 50/50 (4.75% each; rising 0.5%p a year to 13%). Standard monthly income cap KRW 6,590,000 from Jul 2026 (adjusted every July); the lower limit is not modelled.",
      source: PWC("republic-of-korea"),
    }),
    item({
      id: "kr_health", country: "KR", name: { en: "National health insurance", zh: "健康保险", local: "건강보험" },
      employeeBp: 359.5, employerBp: 359.5, base: BASE_ALL, rounding: { mode: "down", unitMinor: 10 },
      note: "2026: 7.19% split 50/50 (3.595% each); health + long-term care total 8.135% per PwC. The monthly premium cap is not modelled.",
      source: PWC("republic-of-korea"),
    }),
    item({
      id: "kr_ltc", country: "KR", name: { en: "Long-term care insurance", zh: "长期护理保险", local: "장기요양보험" },
      employeeBp: 47.24, employerBp: 47.24, base: BASE_ALL, rounding: { mode: "down", unitMinor: 10 },
      note: "Officially = health premium x 13.14% (2026), i.e. ~0.9448% of wage split 50/50 — derived from PwC's 8.135% combined rate; verify. Official amount is computed from the health premium, so it can differ by a few won.",
      source: PWC("republic-of-korea"),
    }),
    item({
      id: "kr_employment_ins", country: "KR", name: { en: "Employment insurance", zh: "雇用保险", local: "고용보험" },
      employeeBp: 90, employerBp: 115, base: BASE_ALL, rounding: { mode: "down", unitMinor: 10 },
      note: "Employee 0.9%; employer 1.15% for employers with < 150 employees (up to 1.75% for larger employers).",
      source: PWC("republic-of-korea"),
    }),
  ],
  idFields: [
    idf("rrn", "Resident registration no.", "居民登记号", "주민등록번호"),
    idf("arc_no", "Alien registration no.", "外国人登录号", "외국인등록번호"),
  ],
  notes: {
    en: "4대보험 example. Industrial accident insurance (산재보험, employer only, by industry) and income tax withholding (간이세액표) are not included.",
    zh: "四大保险示例。工伤保险（雇主负担，按行业）与所得税预扣（简易税额表）未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ TH
const TH: PayrollPreset = {
  country: "TH",
  name: { en: "Thailand", zh: "泰国" },
  currency: "THB",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "th_ssf", country: "TH", name: { en: "Social Security Fund", zh: "社会保险基金", local: "ประกันสังคม" },
      employeeBp: 500, employerBp: 500, wageCeilingMinor: 1750000, capEmployeeMinor: 87500, capEmployerMinor: 87500,
      note: "5% / 5% with a maximum of THB 875 per month each (wage ceiling THB 17,500). The THB 1,650 wage floor is not modelled. Check for temporary rate reductions announced by the SSO.",
      source: PWC("thailand"),
    }),
  ],
  idFields: [
    idf("national_id", "National ID no.", "身份证号码", "เลขประจำตัวประชาชน"),
    idf("sso_no", "Social security no.", "社保号码"),
    idf("tax_id", "Tax ID", "税号"),
  ],
  notes: {
    en: "Personal income tax withholding (PND 1) is not computed.",
    zh: "个人所得税预扣 (PND 1) 未计算。",
  },
};

// ------------------------------------------------------------------------------------------------ ID
const ID: PayrollPreset = {
  country: "ID",
  name: { en: "Indonesia", zh: "印度尼西亚" },
  currency: "IDR",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "id_bpjs_kes", country: "ID", name: { en: "BPJS Health", zh: "BPJS 健康保险", local: "BPJS Kesehatan" },
      employeeBp: 100, employerBp: 400, base: BASE_FIXED, wageCeilingMinor: 1200000000,
      note: "1% employee / 4% employer of basic salary + fixed allowances, base capped at IDR 12,000,000 (since 2020; confirm no 2026 change).",
      source: PWC("indonesia"),
    }),
    item({
      id: "id_jht", country: "ID", name: { en: "Old-age savings (JHT)", zh: "养老储蓄 (JHT)", local: "BPJS TK — JHT" },
      employeeBp: 200, employerBp: 370, base: BASE_FIXED,
      note: "2% employee / 3.7% employer, no wage ceiling.",
      source: PWC("indonesia"),
    }),
    item({
      id: "id_jp", country: "ID", name: { en: "Pension (JP)", zh: "养老金 (JP)", local: "BPJS TK — JP" },
      employeeBp: 100, employerBp: 200, base: BASE_FIXED, wageCeilingMinor: null,
      note: "1% employee / 2% employer. TODO verify: the JP wage cap is adjusted every year by BPJS Ketenagakerjaan — set wageCeilingMinor to the 2026 cap (not confirmed, so no ceiling is applied yet).",
      source: PWC("indonesia"),
    }),
    item({
      id: "id_jkk", country: "ID", name: { en: "Work accident (JKK)", zh: "工伤 (JKK)", local: "BPJS TK — JKK" },
      employeeBp: 0, employerBp: 24, base: BASE_FIXED,
      note: "Employer only, 0.24%–1.74% by risk group — 0.24% (very low risk) used; confirm your group.",
      source: PWC("indonesia"),
    }),
    item({
      id: "id_jkm", country: "ID", name: { en: "Death benefit (JKM)", zh: "死亡保障 (JKM)", local: "BPJS TK — JKM" },
      employeeBp: 0, employerBp: 30, base: BASE_FIXED,
      note: "Employer only, 0.3%. The job-loss benefit (JKP) is funded by reallocation, no extra contribution.",
      source: PWC("indonesia"),
    }),
  ],
  idFields: [
    idf("nik", "NIK (national ID)", "身份证号 (NIK)"),
    idf("npwp", "NPWP (tax no.)", "税号 (NPWP)"),
    idf("bpjs_kes_no", "BPJS Kesehatan no.", "BPJS 健康保险号"),
    idf("bpjs_tk_no", "BPJS Ketenagakerjaan no.", "BPJS 就业保障号"),
  ],
  notes: {
    en: "Base = basic salary + fixed allowances (commission excluded by default). PPh 21 income tax and Tapera are not included.",
    zh: "缴费基数 = 基本工资 + 固定津贴（默认不含提成）。PPh 21 所得税与 Tapera 未包含。",
  },
};

// ------------------------------------------------------------------------------------------------ VN
const VN: PayrollPreset = {
  country: "VN",
  name: { en: "Vietnam", zh: "越南" },
  currency: "VND",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "vn_bhxh", country: "VN", name: { en: "Social insurance", zh: "社会保险", local: "BHXH" },
      employeeBp: 800, employerBp: 1750, base: BASE_FIXED, wageCeilingMinor: null,
      note: "8% employee / 17.5% employer (incl. sickness-maternity 3% and occupational accident 0.5%). Base capped at 20 x the reference level — TODO verify the current reference level and set wageCeilingMinor (VND 2,340,000 x 20 = 46,800,000 when last known).",
      source: PWC("vietnam"),
    }),
    item({
      id: "vn_bhyt", country: "VN", name: { en: "Health insurance", zh: "医疗保险", local: "BHYT" },
      employeeBp: 150, employerBp: 300, base: BASE_FIXED, wageCeilingMinor: null,
      note: "1.5% employee / 3% employer (statutory 4.5% total; the PwC table shows the employee figure as 1 — verify). Base capped at 20 x the reference level — TODO set the ceiling.",
      source: PWC("vietnam"),
    }),
    item({
      id: "vn_bhtn", country: "VN", name: { en: "Unemployment insurance", zh: "失业保险", local: "BHTN" },
      employeeBp: 100, employerBp: 100, base: BASE_FIXED, wageCeilingMinor: null, residency: ["local"],
      note: "1% / 1%, Vietnamese employees only. Base capped at 20 x the regional minimum wage (differs by region I–IV) — TODO set the ceiling.",
      source: PWC("vietnam"),
    }),
  ],
  idFields: [
    idf("cccd", "Citizen ID (CCCD)", "公民身份证 (CCCD)", "CCCD"),
    idf("bhxh_no", "Social insurance no.", "社保号 (BHXH)", "Mã số BHXH"),
    idf("tax_code", "Personal tax code", "个人税号", "MST cá nhân"),
  ],
  notes: {
    en: "Trade union fee (2% employer, if applicable) and PIT withholding are not included. Foreign employees: BHXH/BHYT apply with conditions; BHTN does not.",
    zh: "工会经费（雇主 2%，如适用）及个人所得税预扣未包含。外籍员工有条件适用 BHXH/BHYT，不适用 BHTN。",
  },
};

// ------------------------------------------------------------------------------------------------ US
const US: PayrollPreset = {
  country: "US",
  name: { en: "United States (federal)", zh: "美国（联邦）" },
  currency: "USD",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "us_social_security", country: "US", name: { en: "Social Security (OASDI)", zh: "社会安全税", local: "FICA — Social Security" },
      employeeBp: 620, employerBp: 620, wageCeilingMinor: null,
      note: "6.2% / 6.2% up to the ANNUAL wage base of $184,500 (2026). The engine works per payslip, so no monthly ceiling is set — opt the staff member out once YTD wages reach the base.",
      source: "https://www.irs.gov/taxtopics/tc751",
    }),
    item({
      id: "us_medicare", country: "US", name: { en: "Medicare", zh: "联邦医疗保险税", local: "FICA — Medicare" },
      employeeBp: 145, employerBp: 145,
      note: "1.45% / 1.45%, no wage base. Additional Medicare tax 0.9% (employee only) on wages above $200,000 in the calendar year is not modelled.",
      source: "https://www.irs.gov/taxtopics/tc751",
    }),
  ],
  idFields: [idf("ssn", "SSN", "社会安全号码 (SSN)")],
  notes: {
    en: "Federal and state income tax withholding, FUTA / SUTA and local taxes are NOT computed — use payroll software or enter them as manual deductions.",
    zh: "联邦/州所得税预扣、FUTA/SUTA 以及地方税均未计算 — 请使用薪资软件或以手动扣款输入。",
  },
};

// ------------------------------------------------------------------------------------------------ UK
const UK_SRC = "https://www.gov.uk/guidance/rates-and-thresholds-for-employers-2026-to-2027";
const UK: PayrollPreset = {
  country: "UK",
  name: { en: "United Kingdom", zh: "英国" },
  currency: "GBP",
  retrievedOn: RETRIEVED,
  disclaimer: PRESET_DISCLAIMER,
  items: [
    item({
      id: "uk_ni_employee", country: "UK", name: { en: "National Insurance (employee, Class 1)", zh: "国民保险（雇员 Class 1）" },
      employeeBp: 800, employerBp: 0, wageFloorMinor: 104800, wageCeilingMinor: 418900,
      note: "2026/27 category A: 8% on earnings between the primary threshold (£1,048/month) and the upper earnings limit (£4,189/month), 2% above. APPROXIMATION: the engine applies 8% to the whole (capped) wage once above the threshold, so it OVERSTATES by up to ~£84/month, and ignores the 2% above the UEL. Use HMRC tables / payroll software for exact figures.",
      source: UK_SRC,
    }),
    item({
      id: "uk_ni_employer", country: "UK", name: { en: "National Insurance (employer, Class 1)", zh: "国民保险（雇主 Class 1）" },
      employeeBp: 0, employerBp: 1500,
      note: "2026/27: 15% on earnings above the secondary threshold (£417/month), no upper limit. APPROXIMATION: applied to the whole wage (overstates by ~£62.55/month). Employment Allowance and under-21 / apprentice / veteran reliefs not modelled.",
      source: UK_SRC,
    }),
    item({
      id: "uk_paye", country: "UK", name: { en: "Income tax (PAYE)", zh: "所得税 (PAYE)" },
      method: "bracket", brackets: [],
      note: "PAYE income tax is NOT computed (depends on tax code and cumulative YTD) — enter manually or paste a table.",
      source: UK_SRC,
    }),
  ],
  idFields: [idf("ni_number", "National Insurance number", "国民保险号码")],
  notes: {
    en: "Workplace pension auto-enrolment (min 8% of qualifying earnings, at least 3% from the employer), student loans and statutory pay are not included.",
    zh: "职场养老金自动加入（合格收入至少 8%，其中雇主至少 3%）、学生贷款与法定津贴未包含。",
  },
};

export const PAYROLL_PRESETS: PayrollPreset[] = [MY, SG, HK, TW, CN, JP, KR, TH, ID, VN, US, UK];

/** The preset for an ISO-2 country code (case-insensitive; "GB" is accepted for the UK), or null. */
export function presetFor(country: string): PayrollPreset | null {
  const c = (country || "").trim().toUpperCase();
  const code = c === "GB" ? "UK" : c;
  return PAYROLL_PRESETS.find((p) => p.country === code) ?? null;
}
